import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, Chip, 
    Dialog, DialogTitle, DialogContent, DialogActions, Paper, Select, MenuItem, FormControl, InputLabel,
    InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Grow, Tab, Tabs, Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import StoreIcon from '@mui/icons-material/Store';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../../components/Loading';

function ITSuppliers() {
    const [suppliers, setSuppliers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [filterCountry, setFilterCountry] = useState('');
    const [search, setSearch] = useState('');
    const [filterLetter, setFilterLetter] = useState('All');
    const [tabValue, setTabValue] = useState(0); 

    const [newSupplier, setNewSupplier] = useState({
        name: '', location: '', website: '', phone: '', comment: '', country_id: ''
    });
    
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchSuppliers();
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchSuppliers = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-suppliers`, { headers });
            setSuppliers(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        try {
            await axios.post(`${API_URL}/it-suppliers`, newSupplier, { headers });
            setOpen(false);
            setNewSupplier({ name: '', location: '', website: '', phone: '', comment: '', country_id: '' });
            fetchSuppliers();
        } catch (e) { console.error(e); }
    };

    const filtered = suppliers.filter(s => {
        if (filterLetter !== 'All') {
            if (!(s.name && s.name.toUpperCase().startsWith(filterLetter))) return false;
        }
        if (filterCountry && s.country_id !== filterCountry) return false;
        if (search) {
            const sName = s.name ? s.name.toLowerCase() : '';
            const sLoc = s.location ? s.location.toLowerCase() : '';
            const sq = search.toLowerCase();
            if (!sName.includes(sq) && !sLoc.includes(sq)) return false;
        }
        return true;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    if (loading) return <Loading />;

    const letters = ['All', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];
    const summary = {
        total: suppliers.length
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Box sx={{ mb: 4, mt: 2, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <StoreIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                             IT Suppliers Management
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Manage and track all your IT suppliers in one place
                        </Typography>
                    </Box>
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </Box>
            </Grow>

      

            <Grow in={true} timeout={1000}>
            <Paper elevation={0} sx={{ borderRadius: 4, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 4, overflow: 'hidden', bgcolor: 'white' }}>
                <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 1, flexWrap: 'wrap', gap: 2 }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 60, fontSize: '0.95rem' }, '& .Mui-selected': { color: 'primary.main' } }}>
                        <Tab label="All Suppliers" />
                    </Tabs>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ borderRadius: 2, px: 3, py: 1.2, boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)', textTransform: 'none', fontWeight: 'bold', whiteSpace: 'nowrap', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', mb: 1 }}>
                        New Supplier
                    </Button>
                </Box>

                <Box sx={{ p: 3, bgcolor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: { xs: 'stretch', md: 'center' } }}>
                        <TextField size="medium" placeholder="Search by name or location..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <SearchIcon color="primary" sx={{ mr: 1.5 }} /> }} sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.02)', '& fieldset': { borderColor: 'transparent' }, '&:hover fieldset': { borderColor: '#e0e0e0' }, '&.Mui-focused fieldset': { borderColor: 'primary.main' } } }} />
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FilterListIcon sx={{ color: 'text.secondary' }} />
                            <FormControl size="medium" sx={{ minWidth: 260 }}>
                                <InputLabel>Filter by Country</InputLabel>
                                <Select value={filterCountry} label="Filter by Country" onChange={(e) => setFilterCountry(e.target.value)} sx={{ borderRadius: 3, bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.02)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' } }}>
                                    <MenuItem value=""><em>Global Overview (All Countries)</em></MenuItem>
                                    {countries.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3, pt: 2.5, borderTop: '1px dashed #e0e0e0' }}>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', mr: 2, color: 'primary.main', fontWeight: '800', letterSpacing: 1 }}>FAST INDEX:</Typography>
                        {letters.map(letter => (
                            <Box key={letter} onClick={() => setFilterLetter(letter)} sx={{ cursor: 'pointer', minWidth: letter === 'All' ? 48 : 32, height: 32, px: letter === 'All' ? 1.5 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', fontWeight: filterLetter === letter ? '800' : '600', bgcolor: filterLetter === letter ? '#1976d2' : 'white', color: filterLetter === letter ? 'white' : 'text.secondary', boxShadow: filterLetter === letter ? '0 4px 10px rgba(25, 118, 210, 0.3)' : '0 2px 5px rgba(0,0,0,0.05)', transition: 'all 0.2s', '&:hover': { bgcolor: filterLetter === letter ? '#1565c0' : '#f0f0f0', color: filterLetter === letter ? 'white' : 'text.primary', transform: 'translateY(-2px)' } }}>
                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>{letter}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>
            </Grow>

            <Grow in={true} timeout={1200}>
            <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', width: '100%', bgcolor: 'white' }}>
                <Box sx={{ overflowX: 'auto', width: '100%', display: 'block' }}>
                    <Table sx={{ width: '100%', tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2.5 } }}>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Country</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Location</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Phone</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Website</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((s) => (
                                <TableRow key={s.id} hover onClick={() => navigate(`/it-suppliers/${s.id}`)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fbff !important' } }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{s.name}</TableCell>
                                    <TableCell>{s.country_name || '-'}</TableCell>
                                    <TableCell>{s.location || '-'}</TableCell>
                                    <TableCell>{s.phone || '-'}</TableCell>
                                    <TableCell>
                                        {s.website ? (
                                            <a href={s.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                link
                                            </a>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box onClick={(e) => { e.stopPropagation(); navigate(`/it-suppliers/${s.id}`); }} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eff6ff', color: '#2563eb', borderRadius: 3, p: 1.2, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: '#2563eb', color: 'white', transform: 'scale(1.1)', boxShadow: '0 4px 10px rgba(37,99,235,0.3)' } }}>
                                            <ArrowForwardIcon fontSize="small" />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No suppliers found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>
            </Grow>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    New IT Supplier
                    <CloseIcon onClick={() => setOpen(false)} sx={{ cursor: 'pointer' }} />
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', mb: 2 }}>General Info</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField label="Name" fullWidth variant="outlined" value={newSupplier.name} onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Country</InputLabel>
                                    <Select value={newSupplier.country_id || ''} label="Country" onChange={(e) => setNewSupplier({ ...newSupplier, country_id: e.target.value })}>
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {countries.map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Location" fullWidth variant="outlined" value={newSupplier.location} onChange={(e) => setNewSupplier({...newSupplier, location: e.target.value})} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Website" fullWidth variant="outlined" value={newSupplier.website} onChange={(e) => setNewSupplier({...newSupplier, website: e.target.value})} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Phone" fullWidth variant="outlined" value={newSupplier.phone} onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label="Comment" fullWidth multiline rows={3} variant="outlined" value={newSupplier.comment} onChange={(e) => setNewSupplier({...newSupplier, comment: e.target.value})} />
                            </Grid>
                        </Grid>
                    </Paper>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setOpen(false)} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={!newSupplier.name} sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', boxShadow: '0 8px 16px rgba(25, 118, 210, 0.25)', px: 3 }}>
                        Create Supplier
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ITSuppliers;
